import React from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { CustomerUsageWidget } from "./CustomerUsageWidget";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { createTheme, ThemeProvider } from "@mui/material/styles";
import { CssBaseline, Box, Alert, Typography } from "@mui/material";
import { useTokenRefresh } from "../hooks/useTokenRefresh";

// Create a dedicated query client for the embed
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 60 * 1000, // 1 minute
      retry: 2,
    },
  },
});

export const CustomerUsageEmbedPage: React.FC = () => {
  const { customerId } = useParams<{ customerId: string }>();
  const [searchParams] = useSearchParams();

  // Extract parameters from URL - support both new token and legacy API key
  const jwtToken = searchParams.get("token") || "";
  const apiKey = searchParams.get("apiKey") || ""; // Legacy support
  const projectId = searchParams.get("projectId") || "";
  const theme = (searchParams.get("theme") as "light" | "dark" | "auto") || "light";
  const compact = searchParams.get("compact") === "true";
  const showTierInfo = searchParams.get("showTierInfo") !== "false"; // Default true
  const showReplenishmentInfo = searchParams.get("showReplenishmentInfo") !== "false"; // Default true
  const showCallsGrid = searchParams.get("showCallsGrid") === "true"; // Default false for embed
  const showTokenStatus = searchParams.get("showTokenStatus") === "true"; // Optional token status display

  // Initialize token management
  const {
    isValid: tokenIsValid,
    needsRefresh: tokenNeedsRefresh,
    timeUntilExpiry
  } = useTokenRefresh(jwtToken);

  // Create theme based on the theme parameter
  const muiTheme = React.useMemo(() => {
    return createTheme({
      palette: {
        mode: theme === "dark" ? "dark" : "light",
      },
      typography: {
        fontSize: compact ? 12 : 14,
      },
    });
  }, [theme, compact]);

  // Validation: Require either token or legacy API key + project ID
  if (!customerId) {
    return (
      <Box sx={{ p: 2, color: "error.main" }}>
        Missing required parameter: customerId
      </Box>
    );
  }

  if (!jwtToken && (!apiKey || !projectId)) {
    return (
      <Box sx={{ p: 2, color: "error.main" }}>
        Missing authentication: either 'token' parameter or both 'apiKey' and 'projectId' parameters are required
      </Box>
    );
  }

  // Format time until expiry for display
  const formatTimeUntilExpiry = (milliseconds: number | null): string => {
    if (!milliseconds) return "Unknown";
    const minutes = Math.floor(milliseconds / 60000);
    const seconds = Math.floor((milliseconds % 60000) / 1000);
    return `${minutes}m ${seconds}s`;
  };

  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider theme={muiTheme}>
        <CssBaseline />
        <Box sx={{ 
          p: 0, 
          minHeight: "100vh",
          backgroundColor: "background.default",
          // Remove margin and padding for iframe
          margin: 0,
          "& .MuiPaper-root": {
            boxShadow: "none",
            border: "none",
          }
        }}>
          {/* Optional Token Status Display */}
          {showTokenStatus && jwtToken && (
            <Alert 
              severity={tokenIsValid ? "success" : tokenNeedsRefresh ? "warning" : "error"}
              sx={{ mb: 1, mx: 1 }}
            >
              <Typography variant="caption">
                Token Status: {tokenIsValid ? "Valid" : "Invalid"} | 
                Expires in: {formatTimeUntilExpiry(timeUntilExpiry)} |
                {tokenNeedsRefresh && " Refresh needed"}
              </Typography>
            </Alert>
          )}

          <CustomerUsageWidget
            customerId={decodeURIComponent(customerId)}
            projectId={projectId}
            jwtToken={jwtToken}
            apiKey={apiKey}
            theme={theme}
            compact={compact}
            showTierInfo={showTierInfo}
            showReplenishmentInfo={showReplenishmentInfo}
            showCallsGrid={showCallsGrid}
          />
        </Box>
      </ThemeProvider>
    </QueryClientProvider>
  );
};
